import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import Fuse from 'fuse.js';
import { commandPaletteItems } from 'utils/commandPaletteItems';
import { CommandPaletteItem } from 'types/commandPalette';
import { useUserPreferencesStore } from 'stores/userPreferencesStore';
import { isBetaOnlyPath } from 'utils/betaFeatures';
import SearchOverlay, { SearchOverlayHandle } from 'components/SearchOverlay';
import styles from './CommandPalette.module.scss';

const SECTION_ORDER = ['Pages', 'Actions', 'Reports', 'Account'];

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<SearchOverlayHandle>(null);
  const navigate = useNavigate();
  const betaEnabled = useUserPreferencesStore((s) => s.betaEnabled);

  const filteredItems = useMemo(
    () =>
      commandPaletteItems.filter(
        (item) => betaEnabled || !isBetaOnlyPath(item.path),
      ),
    [betaEnabled],
  );

  const fuse = useMemo(
    () =>
      new Fuse(filteredItems, {
        keys: [
          { name: 'title', weight: 0.6 },
          { name: 'keywords', weight: 0.3 },
          { name: 'section', weight: 0.1 },
        ],
        threshold: 0.4,
        includeScore: true,
        minMatchCharLength: 1,
      }),
    [filteredItems],
  );

  const results: CommandPaletteItem[] = useMemo(() => {
    if (!query.trim()) {
      return filteredItems;
    }
    return fuse.search(query).map((r) => r.item);
  }, [filteredItems, fuse, query]);

  const groupedResults = useMemo(() => {
    const groups: Record<string, CommandPaletteItem[]> = {};
    for (const item of results) {
      if (!groups[item.section]) {
        groups[item.section] = [];
      }
      groups[item.section].push(item);
    }
    return SECTION_ORDER.filter((s) => groups[s]).map((section) => ({
      section,
      items: groups[section],
    }));
  }, [results]);

  const flatResults = useMemo(
    () => groupedResults.flatMap((g) => g.items),
    [groupedResults],
  );

  const selectItem = useCallback(
    (item: CommandPaletteItem) => {
      overlayRef.current?.skipNextHistoryBack();
      onClose();
      navigate(item.path, { replace: true });
    },
    [navigate, onClose],
  );

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setActiveIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    const activeEl = listRef.current?.querySelector(
      `[data-index="${activeIndex}"]`,
    );
    activeEl?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex((i) => (i + 1) % flatResults.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex(
            (i) => (i - 1 + flatResults.length) % flatResults.length,
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (flatResults[activeIndex]) {
            selectItem(flatResults[activeIndex]);
          }
          break;
      }
    },
    [flatResults, activeIndex, selectItem],
  );

  let runningIndex = 0;

  return (
    <SearchOverlay
      ref={overlayRef}
      isOpen={isOpen}
      onClose={onClose}
      query={query}
      onQueryChange={setQuery}
      placeholder="Search pages, actions, reports..."
      shortcutHint="ESC"
      onKeyDown={handleKeyDown}
    >
      <div ref={listRef} className={styles.resultsList}>
        {flatResults.length === 0 ? (
          <div className={styles.empty}>No results found</div>
        ) : (
          groupedResults.map((group) => {
            const sectionItems = group.items.map((item) => {
              const idx = runningIndex++;
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  data-index={idx}
                  className={`${styles.resultItem} ${idx === activeIndex ? styles.active : ''}`}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => selectItem(item)}
                  type="button"
                >
                  <Icon size={18} className={styles.resultIcon} />
                  <span className={styles.resultTitle}>{item.title}</span>
                </button>
              );
            });
            return (
              <div key={group.section} className={styles.section}>
                <div className={styles.sectionLabel}>{group.section}</div>
                {sectionItems}
              </div>
            );
          })
        )}
      </div>
    </SearchOverlay>
  );
}

export default CommandPalette;
